import { useContext, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { TestContext } from "../../context/test_context";
import { MetricsContext } from "../../context/metrics_context";
import LoadBox from "../../components/global/LoadBox";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import TestButtonSet from "../../components/test/TestButtonSet";
import TestVideoCard from "../../components/test/TestVideoCard";
import {
  formatTestDate,
  getTestVariantStatus,
  TestVariantStatus,
} from "../../utils/time_calculators";
import { ThumbnailTitleTest, TestStatistics } from "../../context/models";
import useAuth from "../../hooks/auth_hook";
import { Button } from "../../components/ui/button";
import { Spinner } from "../../components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";

type TestRow = {
  id: string;
  thumbnail_and_title: ThumbnailTitleTest;
  status: TestVariantStatus;
  start_date: string;
  end_date: string;
  views: string;
  likes: string;
  comments: string;
  shares: string;
  ctr: string;
  impressions: string;
  avd: string;
  avp: string;
  avd_ctr: string;
  subs: string;
  members: string;
  revenue: string;
};

const STATUS_CONFIG: Record<
  TestVariantStatus,
  { label: string; className: string }
> = {
  scheduled: {
    label: "Scheduled",
    className: "border-border bg-muted text-muted-foreground",
  },
  running: {
    label: "Running",
    className: "border-success/40 bg-success/10 text-success-dark",
  },
  completed: {
    label: "Completed",
    className: "border-primary/40 bg-primary/10 text-primary",
  },
};

function StatusChip({ status }: { status: TestVariantStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.scheduled;
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium",
        cfg.className
      )}
    >
      {cfg.label}
    </span>
  );
}

function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return "N/A";
  return num.toLocaleString();
}

function formatPercentage(num: number | null | undefined): string {
  if (num === null || num === undefined) return "N/A";
  return `${(num * 100).toFixed(2)}%`;
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "N/A";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function TestGrid({ tests }: { tests: ThumbnailTitleTest[] }) {
  const { getTestStatistics, metrics_loading } = useContext(MetricsContext);
  const [statistics, setStatistics] = useState<Record<string, TestStatistics>>({});
  const [fetchErrors, setFetchErrors] = useState<Record<string, boolean>>({});
  const columnHelper = createColumnHelper<TestRow>();

  useEffect(() => {
    tests.forEach((test) => {
      if (!test.id) return;
      getTestStatistics(test.id).then(
        ({
          statistics: stats,
          error,
        }: {
          statistics: TestStatistics | null;
          error: boolean;
        }) => {
          if (error) {
            setFetchErrors((prev) => ({ ...prev, [test.id]: true }));
          } else if (stats) {
            setStatistics((prev) => ({ ...prev, [test.id]: stats }));
          }
        }
      );
    });
  }, [tests, getTestStatistics]);

  const rows: TestRow[] = useMemo(() => {
    return tests.map((test, index) => {
      const testStats = statistics[test.id];
      const fetchFailed = fetchErrors[test.id] === true;
      const placeholder = fetchFailed ? "Error" : metrics_loading ? "Loading..." : "N/A";
      const status = getTestVariantStatus(test);

      let startDate: string;
      let endDate: string;
      if (status === "scheduled") {
        startDate = test.time_to_test
          ? `Starts ${formatTestDate(test.time_to_test)}`
          : "Pending";
        endDate = "—";
      } else if (status === "running") {
        startDate = test.activated ? formatTestDate(test.activated) : "—";
        if (test.activated && test.duration) {
          const expectedEnd = new Date(
            new Date(test.activated).getTime() + test.duration * 1000
          );
          endDate = `Ends ${formatTestDate(expectedEnd.toISOString())}`;
        } else {
          endDate = "—";
        }
      } else {
        startDate = test.activated ? formatTestDate(test.activated) : "—";
        endDate = test.deactivated ? formatTestDate(test.deactivated) : "—";
      }

      return {
        id: test.id || `test-${index}`,
        thumbnail_and_title: test,
        status,
        start_date: startDate,
        end_date: endDate,
        views: testStats ? formatNumber(testStats.view_count) : placeholder,
        likes: testStats ? formatNumber(testStats.like_count) : placeholder,
        comments: testStats ? formatNumber(testStats.comment_count) : placeholder,
        shares: testStats ? formatNumber(testStats.shares) : placeholder,
        ctr: testStats ? formatPercentage(testStats.ctr) : placeholder,
        impressions: testStats ? formatNumber(testStats.impressions) : placeholder,
        avd: testStats ? formatDuration(testStats.avd) : placeholder,
        avp: testStats ? formatPercentage(testStats.avp) : placeholder,
        avd_ctr: testStats
          ? testStats.avd_ctr !== null && testStats.avd_ctr !== undefined
            ? testStats.avd_ctr.toFixed(2)
            : "N/A"
          : placeholder,
        subs: testStats ? formatNumber(testStats.subs_gained) : placeholder,
        members: testStats ? formatNumber(testStats.members) : placeholder,
        revenue: testStats
          ? testStats.revenue !== null && testStats.revenue !== undefined
            ? `$${testStats.revenue.toFixed(2)}`
            : "N/A"
          : placeholder,
      };
    });
  }, [tests, statistics, fetchErrors, metrics_loading]);

  const columns = useMemo<ColumnDef<TestRow, any>[]>(
    () => [
      columnHelper.accessor("thumbnail_and_title", {
        header: "Test",
        cell: (info) => <TestVideoCard Video={info.getValue()} />,
        size: 250,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusChip status={info.getValue()} />,
        size: 120,
      }),
      columnHelper.accessor("start_date", { header: "Start Date", size: 180 }),
      columnHelper.accessor("end_date", { header: "End Date", size: 180 }),
      columnHelper.accessor("views", { header: "Views" }),
      columnHelper.accessor("likes", { header: "Likes" }),
      columnHelper.accessor("comments", { header: "Comments" }),
      columnHelper.accessor("shares", { header: "Shares" }),
      columnHelper.accessor("ctr", { header: "Click Through Rate" }),
      columnHelper.accessor("impressions", { header: "Impressions" }),
      columnHelper.accessor("avd", { header: "Average View Duration", size: 180 }),
      columnHelper.accessor("avp", { header: "Average View %" }),
      columnHelper.accessor("avd_ctr", { header: "AVD × CTR" }),
      columnHelper.accessor("subs", { header: "Subscribers Gained" }),
      columnHelper.accessor("members", { header: "New Members" }),
      columnHelper.accessor("revenue", { header: "Increase in Revenue" }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="p-4 w-full">
      <div className="overflow-x-auto rounded-lg border border-border shadow-sm">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    style={
                      header.getSize() !== 150 ? { width: header.getSize() } : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-muted/30 transition-colors">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-3 whitespace-nowrap text-sm text-foreground"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TestPage() {
  const { testid } = useParams();
  const { getTestGroup, loading } = useContext(TestContext);
  const { user } = useAuth();
  const [testgroup, setTestGroup] = useState<any>(null);
  const [tests, setTests] = useState<ThumbnailTitleTest[]>([]);
  const [collecting, setCollecting] = useState(false);
  const [metricsKey, setMetricsKey] = useState(0);

  const calculateCurrentTest = (tests: ThumbnailTitleTest[]) => {
    if (tests.filter((test) => test.tested).length === tests.length) {
      return tests[tests.length - 1];
    }
    return tests.find((test) => test.active) || tests[0];
  };
  const currentTest = calculateCurrentTest(tests);

  const studioDataFetched =
    tests.length > 0 && tests.every((t) => t.yt_studio_metrics_fetched);

  useEffect(() => {
    getTestGroup(testid).then((tg: any) => {
      setTestGroup(tg);
      setTests(tg.tests);
    });
  }, []);

  useEffect(() => {
    const handleDone = (event: any) => {
      setCollecting(false);
      const { collected, total } = event.detail || {};
      if (collected === 0) {
        toast.warning(
          "No Studio data found. The video may not have enough views yet, or you may not be logged into YouTube Studio."
        );
        return;
      }
      const msg =
        collected < total
          ? `Collected data for ${collected} of ${total} videos. The rest had no Studio analytics available.`
          : `Studio data collected successfully for ${collected} video${
              collected !== 1 ? "s" : ""
            }.`;
      if (collected < total) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      getTestGroup(testid).then((tg: any) => {
        setTestGroup(tg);
        setTests(tg.tests);
        setMetricsKey((k) => k + 1);
      });
    };
    const handleError = (event: any) => {
      setCollecting(false);
      const msg = event.detail?.error || "An unknown error occurred.";
      const persistent = msg.includes("signed into a different");
      toast.error(msg, persistent ? { duration: Infinity } : undefined);
    };
    document.addEventListener("nailthumb:done", handleDone);
    document.addEventListener("nailthumb:error", handleError);
    return () => {
      document.removeEventListener("nailthumb:done", handleDone);
      document.removeEventListener("nailthumb:error", handleError);
    };
  }, [testid, getTestGroup]);

  const handleCollectStudioData = () => {
    const videoIds = Array.from(new Set(tests.map((t) => t.video_id)));
    if (videoIds.length === 0) return;
    setCollecting(true);
    document.dispatchEvent(
      new CustomEvent("nailthumb:collect", {
        detail: { videoIds, userSub: user?.sub },
      })
    );
  };

  if (!testgroup) {
    return <LoadBox loading={true}>Loading</LoadBox>;
  }

  const collectButton = (
    <Button
      variant={studioDataFetched ? "default" : "secondary"}
      disabled={collecting}
      onClick={handleCollectStudioData}
      className="gap-2"
    >
      {collecting ? (
        <Spinner size="sm" />
      ) : studioDataFetched ? (
        <CheckCircle2 className="h-4 w-4" />
      ) : null}
      {collecting
        ? "Collecting..."
        : studioDataFetched
        ? "Studio Data Collected"
        : "Collect Studio Data"}
    </Button>
  );

  return (
    <LoadBox loading={loading}>
      <div className="flex-grow p-4">
        <div className="mb-6 flex justify-start pl-4 gap-2">
          <TestButtonSet testgroup={testgroup} setTestGroup={setTestGroup} />
        </div>
        <div className="mb-6 flex justify-start pl-4 gap-2 items-center flex-wrap">
          <Button asChild>
            <a
              href={`https://www.youtube.com/watch?v=${currentTest?.video_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Link to video
            </a>
          </Button>
          {studioDataFetched ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{collectButton}</span>
                </TooltipTrigger>
                <TooltipContent>
                  Studio analytics have been collected for all tests. You can collect again to refresh the data.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            collectButton
          )}
        </div>
        <TestGrid key={metricsKey} tests={tests} />
      </div>
    </LoadBox>
  );
}

export default TestPage;
